"use client";

import type { LocationFilter } from "@/lib/api/types";
import {
  DEFAULT_DISTANCE,
  LOCATION_CATEGORY_BADGE,
  normalizeDistanceForDisplay,
} from "@/features/search/lib/filter-options";
import { cn } from "@/lib/utils";
import { FilterValueActions } from "../actions";
import { DistanceDropdown } from "./internal/distance-dropdown";
import { RangeModeDropdown } from "./internal/range-mode-dropdown";
import { ScopeDropdown } from "./internal/scope-dropdown";

export function FilterLocationInput({
  location,
  defaultDistance,
  onChange,
  onRemove,
}: {
  location: LocationFilter;
  /**
   * Optional default distance to seed when the user toggles a chip from
   * "Exact Location" to "Area Based". This is purely a UX hint — it does
   * NOT affect display when the chip is in exact mode. The chip's render
   * mode is driven solely by `location.distance`: present = area, absent
   * = exact. This mirrors the backend query builder, which only emits a
   * geo_distance clause when per-item `distance` is set.
   */
  defaultDistance?: string | null;
  onChange: (updated: LocationFilter) => void;
  onRemove: () => void;
}) {
  const category = location.category ?? "region";
  const name =
    location.display_name ??
    location.locality ??
    location.region ??
    location.country ??
    "";
  const isCity = category === "city";
  const hasGeo = location.lat != null && location.lon != null;
  const canDoArea = isCity && hasGeo;
  // Render mode is driven ONLY by per-item distance — never the top-level.
  // Falling back to the top-level here would force every chip into "area"
  // mode after a search loads (since top-level `distance` defaults to 25mi),
  // and the user can never get back to exact display.
  const rangeMode: "exact" | "area" =
    location.distance && canDoArea ? "area" : "exact";
  const distance = normalizeDistanceForDisplay(location.distance);
  const seedWhenSwitchingToArea = normalizeDistanceForDisplay(
    location.distance ?? defaultDistance ?? DEFAULT_DISTANCE,
  );
  const isExcluded = location.mode === "exclude";
  const scope = location.scope ?? "cp";

  return (
    <div
      className={cn(
        "border rounded-md overflow-clip",
        isExcluded ? "border-destructive/30" : "border-border",
      )}
    >
      <div className="flex items-center justify-between bg-contrast/4 h-[34px] px-2">
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          {LOCATION_CATEGORY_BADGE[category] && (
            <span className="text-[9px] font-normal text-foreground/50 tracking-[-0.09px] whitespace-nowrap uppercase shrink-0">
              {LOCATION_CATEGORY_BADGE[category]}
            </span>
          )}
          <span
            className={cn(
              "text-[13px] font-normal truncate tracking-[-0.13px]",
              isExcluded
                ? "text-destructive/80 dark:text-red-400/80 line-through"
                : "text-foreground/80",
            )}
          >
            {name}
          </span>
        </div>
        <FilterValueActions
          excluded={isExcluded}
          required={location.required ?? false}
          onToggleRequired={() =>
            onChange({ ...location, required: !location.required })
          }
          onToggleExclude={() =>
            onChange({ ...location, mode: isExcluded ? "include" : "exclude" })
          }
          onRemove={onRemove}
        />
      </div>
      <div className="flex items-center justify-between bg-contrast/8 border-t border-border h-7 px-2">
        <div className="flex items-center gap-1.5">
          {canDoArea ? (
            <>
              <RangeModeDropdown
                value={rangeMode}
                onChange={(mode) => {
                  if (mode === "exact") {
                    onChange({ ...location, distance: undefined });
                  } else {
                    onChange({
                      ...location,
                      distance: seedWhenSwitchingToArea,
                    });
                  }
                }}
              />
              {rangeMode === "area" && (
                <>
                  <div className="bg-contrast/20 h-2.5 w-px shrink-0" />
                  <DistanceDropdown
                    value={distance}
                    onChange={(d) => onChange({ ...location, distance: d })}
                  />
                </>
              )}
            </>
          ) : (
            <span className="text-[11px] tracking-[-0.1px] text-ai whitespace-nowrap">
              Exact Location
            </span>
          )}
        </div>
        <ScopeDropdown
          value={scope}
          onChange={(v) => onChange({ ...location, scope: v })}
        />
      </div>
    </div>
  );
}
